import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';

import { ReauthModal } from '@/components/ReauthModal';
import { useContentSettled } from '@/lib/a11y/useContentSettled';

import { CommandPalette } from './CommandPalette';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

/*
 * §1 — "Sidebar is collapsible and persists its state."
 */
const COLLAPSED_KEY = 'sjuni.admin.sidebarCollapsed.v1';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * docs/05-ADMIN-DASHBOARD.md §1 — the signed-in frame.
 *
 * One sidebar, rendered twice: a rail from `lg` up, and a drawer below it. §6.8
 * says the admin is "responsive down to tablet" with "phone support for the
 * inbox and dashboard only" — which is a statement about which *screens* work
 * on a phone, not permission for the navigation to be unreachable there.
 */
export function AdminLayout() {
  // The axe gate waits on this before it scans; see the hook.
  useContentSettled();

  const { t } = useTranslation();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;

      try {
        window.localStorage.setItem(COLLAPSED_KEY, String(next));
      } catch {
        // Not remembering the preference is not worth failing over.
      }

      return next;
    });
  }, []);

  // §1 — ⌘K on a Mac, Ctrl+K elsewhere. Captured at the document so it works
  // from inside an editor, which is where someone is when they want it.
  // Escape closes the mobile drawer for the same reason: focus is on a link
  // inside it, not on the scrim.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setPaletteOpen((current) => !current);

        return;
      }

      if (event.key === 'Escape') {
        setDrawerOpen(false);
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // A drawer left open across a navigation covers the screen it opened.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-page">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-navy-600 focus:px-4 focus:py-2 focus:text-white"
      >
        {t('common.skipToContent')}
      </a>

      <Header onOpenPalette={() => setPaletteOpen(true)} onOpenDrawer={() => setDrawerOpen(true)} />

      <div className="flex">
        <div className="sticky top-16 hidden h-[calc(100vh-4rem)] lg:block">
          <Sidebar collapsed={collapsed} onToggle={toggleCollapsed} />
        </div>

        {drawerOpen ? (
          /*
           * The scrim is a click target and nothing else. It carries no
           * `onKeyDown`: a div that is not focusable never receives one, so
           * such a handler is dead code that reads as keyboard support. Escape
           * is handled at the document, below.
           */
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            className="fixed inset-0 z-40 bg-[rgba(26,24,20,0.45)] lg:hidden"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setDrawerOpen(false);
              }
            }}
          >
            <Sidebar
              collapsed={false}
              onToggle={() => setDrawerOpen(false)}
              onNavigate={() => setDrawerOpen(false)}
              className="h-full w-64"
            />
          </div>
        ) : null}

        <main id="main" className="min-w-0 flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* Outside the outlet: §6.7's modal has to survive whatever screen is
          mounted, because that screen is holding unsaved work. */}
      <ReauthModal />
    </div>
  );
}
